import type { OAuthProvider } from "../api/auth";
import type { StytchClient } from "./projectExchange";

// Sign-in methods by their Stytch names: how copy names them, which ones an organization still
// requires, and how one is started. Login, the project URL step-up and the legacy project
// switchers share this module, so a method reads and starts the same way everywhere.

export const PROVIDER_LABELS: Record<OAuthProvider, string> = {
    google_oauth: "Google",
    microsoft_oauth: "Microsoft",
};

export const AUTH_METHOD_LABELS: Record<string, string> = {
    ...PROVIDER_LABELS,
    magic_link: "an email sign-in link",
    email_otp: "an emailed sign-in code",
    sso: "single sign-on",
};

export function describeAuthMethods(methods: readonly string[]): string {
    const labels = methods.map((method) => AUTH_METHOD_LABELS[method] ?? method);
    if (labels.length <= 1) {
        return labels[0] ?? "a different sign-in method";
    }
    return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

/** "This project requires signing in with Google." */
export function signInRequirement(methods: readonly string[]): string {
    return `This project requires signing in with ${describeAuthMethods(methods)}.`;
}

interface OrganizationAuthState {
    member_authenticated: boolean;
    primary_required?: { allowed_auth_methods?: readonly string[] } | null;
    organization: { allowed_auth_methods?: readonly string[] };
}

/**
 * The methods an organization requires before this session can enter it, or null when it can.
 * Stytch reports member_authenticated === false when the member's current sign-in method is not
 * one the organization allows; entering it anyway yields a session the backend rejects (login's
 * intermediate exchange) or ends this one (a session exchange), so callers stop first.
 */
export function requiredAuthMethods(organization: OrganizationAuthState): string[] | null {
    if (organization.member_authenticated) {
        return null;
    }
    return [
        ...(organization.primary_required?.allowed_auth_methods ??
            organization.organization.allowed_auth_methods ??
            []),
    ];
}

export type DirectSignInMethod = OAuthProvider | "sso";

/** The method a step-up starts without the login picker: the only one named, when it is OAuth or
 *  SSO. Several methods, or one that sends an email, leave the choice to the picker. */
export function directSignInMethod(
    methods: readonly string[] | null | undefined,
): DirectSignInMethod | null {
    const [only, ...others] = methods ?? [];
    return others.length === 0 &&
        (only === "google_oauth" || only === "microsoft_oauth" || only === "sso")
        ? only
        : null;
}

/** The login picker's query parameter naming the organization a project URL's step-up is for. */
export const REQUESTED_ORGANIZATION_PARAM = "organization";

export interface SsoConnection {
    connection_id: string;
    /** The organization's name for it, which a choice between several shows. */
    display_name?: string;
}

interface OrganizationSsoState {
    organization: {
        organization_id: string;
        sso_active_connections?: readonly SsoConnection[];
        /** The connection single sign-on starts when the organization has several. */
        sso_default_connection_id?: string | null;
    };
}

/**
 * The single sign-on connections that sign in to `organizationId`, from the person's own
 * discovered organizations: its only one, or the default it names among several, as the one to
 * start; every active one when it names no default, for the person to choose by name; none when
 * those organizations do not include it exactly once. The id only picks among what Stytch listed
 * for this person, so an id from a URL starts nothing it does not list. Never a connection found
 * by the address alone: that may belong to another of the person's organizations, and signing in
 * there returns to that organization's project, not the one asked for.
 */
export function organizationSsoConnections(
    organizations: readonly OrganizationSsoState[],
    organizationId: string,
): SsoConnection[] {
    const matches = organizations.filter(
        (org) => org.organization.organization_id === organizationId,
    );
    if (matches.length !== 1) return [];
    const { sso_active_connections: active = [], sso_default_connection_id: preferred } =
        matches[0].organization;
    const named = active.find((connection) => connection.connection_id === preferred);
    return named ? [named] : [...active];
}

const AUTH_PROVIDER_KEY = "primepoint_auth_provider";

/** The OAuth provider the last sign-in started with, for the login page's email label. */
export function readStoredAuthProvider(): OAuthProvider | null {
    try {
        const stored = localStorage.getItem(AUTH_PROVIDER_KEY);
        if (stored === "google_oauth" || stored === "microsoft_oauth") {
            return stored;
        }
    } catch {
        // localStorage might be blocked in iframe
    }
    return null;
}

export function clearStoredAuthProvider() {
    try {
        localStorage.removeItem(AUTH_PROVIDER_KEY);
    } catch {
        // localStorage might be blocked in iframe
    }
}

function persistAuthProvider(provider: OAuthProvider) {
    try {
        localStorage.setItem(AUTH_PROVIDER_KEY, provider);
    } catch {
        // localStorage might be blocked in iframe
    }
}

/**
 * Leaves for a sign-in method's provider. OAuth runs Stytch's discovery flow, so the project is
 * picked on return to `redirectUrl`. SSO opens `ssoConnectionId`, one of
 * `organizationSsoConnections` for the organization a step-up is for; a plain sign-in, which
 * knows only the address, opens the first connection Stytch discovers for `email`. The caller
 * keeps its own return path (`storeAuthReturn`) before calling.
 */
export async function startSignInRedirect(
    client: StytchClient,
    method: DirectSignInMethod,
    {
        redirectUrl,
        email,
        ssoConnectionId,
    }: { redirectUrl: string; email?: string; ssoConnectionId?: string },
): Promise<void> {
    if (method === "sso") {
        clearStoredAuthProvider();
        let connectionId = ssoConnectionId;
        if (!connectionId && email)
            connectionId = (await client.sso.discoverConnections(email)).connections[0]
                ?.connection_id;

        if (!connectionId) {
            throw new Error("No SSO connection available for this email.");
        }

        // Same-tab redirects retain sessionStorage. Their callback must match
        // the already-registered bare URL; unlike XHR they cannot retry a
        // Stytch callback-configuration rejection here.
        await client.sso.start({
            connection_id: connectionId,
            login_redirect_url: redirectUrl,
            signup_redirect_url: redirectUrl,
        });
        return;
    }
    persistAuthProvider(method);
    const options = {
        discovery_redirect_url: redirectUrl,
        provider_params: email ? { login_hint: email } : undefined,
    };
    if (method === "google_oauth") {
        await client.oauth.google.discovery.start(options);
    } else {
        await client.oauth.microsoft.discovery.start(options);
    }
}
